/* =====================================================================
   Horizontal Scroller Product controller (fallback driver)
   ---------------------------------------------------------------------
   Drives the horizontal pan ONLY on browsers without CSS scroll-driven
   animations (Firefox, as of June 2026). On supported browsers (Chrome,
   Edge, Safari 18+), CSS handles the pan and this controller exits at
   startup.

   The CSS @supports gate and this CSS.supports check stay in sync. If
   one says "use CSS," the other must agree, or both run and fight.

   Algorithm:
     1. Read section's getBoundingClientRect().
     2. Compute progress = -rect.top / (section.offsetHeight - stage.height).
        Clamped [0, 1]. 0 = section just pinned. 1 = section about to
        unpin.
     3. translate3d the scene by -progress * (scrollWidth - viewportWidth).

   Scroll listener is attached only while the section is near the
   viewport (IntersectionObserver gate, 50% rootMargin). rAF coalesces
   multiple scroll events per frame to a single transform write.
   ===================================================================== */
(function () {
  'use strict';

  const SUPPORTS_VIEW_TIMELINE = typeof CSS !== 'undefined'
    && typeof CSS.supports === 'function'
    && CSS.supports('animation-timeline', 'view()');

  function init() {
    /* CSS handles it on this browser. Skip everything. */
    if (SUPPORTS_VIEW_TIMELINE) return;

    /* Reduced-motion users get the vertical stack via CSS @media. JS
       short-circuits so we don't write transforms over a static layout. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const section = document.querySelector('[data-hscroll-section]');
    if (!section) return;
    const stage = section.querySelector('[data-hscroll-stage]');
    const scene = section.querySelector('[data-hscroll-scene]');
    if (!stage || !scene) return;

    const state = {
      isActive: false,
      rafId: null,
    };

    function update() {
      state.rafId = null;
      const rect = section.getBoundingClientRect();
      const scrollable = section.offsetHeight - stage.clientHeight;
      if (scrollable <= 0) return;
      let progress = -rect.top / scrollable;
      if (progress < 0) progress = 0;
      else if (progress > 1) progress = 1;
      const maxX = scene.scrollWidth - stage.clientWidth;
      scene.style.transform = 'translate3d(' + (-progress * maxX) + 'px, 0, 0)';
    }

    function onScroll() {
      if (state.rafId !== null) return;
      state.rafId = window.requestAnimationFrame(update);
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !state.isActive) {
            state.isActive = true;
            window.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onScroll, { passive: true });
            update();
          } else if (!entry.isIntersecting && state.isActive) {
            state.isActive = false;
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
          }
        });
      },
      { rootMargin: '50%' }
    );

    observer.observe(section);
  }

  /* Card text reveal: fires once per card when it crosses 20% visible.
     Sets data-revealed="true"; CSS slides + fades the text content in.
     Unobserves per-item after firing so the reveal sticks. */
  function initCardAnimations() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof IntersectionObserver !== 'function') return;

    const items = document.querySelectorAll('[data-hscroll-section] .c-horizontal-scroller-product__item');
    if (!items.length) return;

    const revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.dataset.revealed = 'true';
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    items.forEach(function (item) {
      revealObserver.observe(item);
    });
  }

  function bootstrap() {
    init();
    initCardAnimations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
